import type { ItemListSummaryRow, SortKey } from "../types/listItemSummary"

export function inventoryValue(row: ItemListSummaryRow): number {
  return Math.max(row.soh, 0) * row.avgCost
}

export function marginPercent(row: ItemListSummaryRow): number | null {
  if (!row.sellingPrice) return null
  return ((row.sellingPrice - row.avgCost) / row.sellingPrice) * 100
}

export function formatNumberId(value: number): string {
  return Number(value).toLocaleString("id-ID")
}

/** Ringkas angka besar untuk KPI (mirip mockup: 1.82B, 94M) */
export function formatCompactId(n: number): string {
  const abs = Math.abs(n)
  if (abs >= 1_000_000_000) {
    const v = n / 1_000_000_000
    return `${v >= 10 ? v.toFixed(0) : v.toFixed(2).replace(/\.?0+$/, "")}B`
  }
  if (abs >= 1_000_000) {
    return `${Math.round(n / 1_000_000)}M`
  }
  if (abs >= 1_000) {
    return `${Math.round(n / 1_000)}K`
  }
  return formatNumberId(Math.round(n))
}

export function getSortValue(row: ItemListSummaryRow, key: SortKey): string | number {
  switch (key) {
    case "code":
      return row.itemCode.toLowerCase()
    case "name":
      return row.itemName.toLowerCase()
    case "category":
      return row.category.toLowerCase()
    case "location":
      return row.location.toLowerCase()
    case "soh":
      return row.soh
    case "avgCost":
      return row.avgCost
    case "lastPrice":
      return row.lastPrice
    case "sellingPrice":
      return row.sellingPrice
    case "inventoryValue":
      return inventoryValue(row)
    case "margin":
      return marginPercent(row) ?? -Infinity
    case "stockStatus":
      return row.stockStatus.toLowerCase()
    case "movementStatus":
      return row.movementStatus.toLowerCase()
    default:
      return ""
  }
}

export type ListItemSummaryKpi = {
  totalSku: number
  totalSoh: number
  inventoryValueTotal: number
  lowStock: number
  outOfStock: number
  deadStockValue: number
  avgMarginPct: number | null
}

export function computeKpi(rows: ItemListSummaryRow[]): ListItemSummaryKpi {
  const totalSku = rows.length
  let totalSoh = 0
  let inventoryValueTotal = 0
  let lowStock = 0
  let outOfStock = 0
  let deadStockValue = 0
  const margins: number[] = []

  for (const r of rows) {
    totalSoh += r.soh
    inventoryValueTotal += inventoryValue(r)
    if (r.stockStatus === "Low Stock") lowStock += 1
    if (r.stockStatus === "Out of Stock") outOfStock += 1
    if (r.stockStatus === "Dead Stock") deadStockValue += inventoryValue(r)
    const m = marginPercent(r)
    if (m !== null) margins.push(m)
  }

  const avgMarginPct =
    margins.length > 0 ? margins.reduce((a, b) => a + b, 0) / margins.length : null

  return {
    totalSku,
    totalSoh,
    inventoryValueTotal,
    lowStock,
    outOfStock,
    deadStockValue,
    avgMarginPct
  }
}
